import React, { useState, useRef, useEffect } from "react";
import ProfileCard from "./ProfileCard";

// `notesState`: "ready" once the member's saved notes have arrived, "loading"
// before, "failed" if they could not be fetched. The note box only takes typing
// when "ready", so a note typed over a not-yet-loaded box can't replace the
// saved one.
function DirectoryRow({ entry, isExpanded, onToggle, note, onSaveNote, rowId, notesState = "ready" }) {
  const [draft, setDraft] = useState(note);
  const debounceRef = useRef(null);
  // Follow the saved note (it can arrive after the row first draws) until the
  // member starts typing; from then on their words win.
  const hasTypedRef = useRef(false);
  useEffect(() => {
    if (!hasTypedRef.current) setDraft(note);
  }, [note]);

  const hasContent =
    entry.has_profile &&
    entry.profile &&
    (entry.profile.profile_picture_url ||
      entry.profile.email ||
      entry.profile.phone_number ||
      entry.profile.state_of_residency ||
      entry.profile.job_or_passion ||
      entry.profile.description);

  const initial = (entry.forenames || "?").charAt(0).toUpperCase();
  const NameTag = hasContent ? "button" : "div";

  const handleNoteChange = (e) => {
    const val = e.target.value;
    hasTypedRef.current = true;
    setDraft(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSaveNote(val), 800);
  };

  return (
    <div id={rowId} className={`relative my-[4px] mx-[8px] rounded-[10px] transition-all duration-200 hover:-translate-y-[2px] hover:shadow-[0_4px_16px_rgba(0,9,28,0.14)] dark:hover:shadow-[0_4px_16px_rgba(0,0,0,0.4)]${hasContent ? " z-[1] shadow-[0_2px_10px_rgba(0,9,28,0.08)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.3)] border-l-[3px] border-l-dupontGold" : ""}`}>
      {/* Compact row. On a phone the note box sits under the name, so long
          names aren't squeezed into a sliver. */}
      <div
        className={`flex flex-col items-stretch sm:flex-row sm:items-center gap-y-[8px] py-[12px] px-[20px] border-b border-dupontCreamDk dark:border-darkGold/10 ${
          hasContent
            ? "cursor-pointer hover:bg-dupontBlue/5 dark:hover:bg-darkGold/[0.06] rounded-[10px]"
            : ""
        }`}
      >
        {/* Left side: avatar + name (a button that opens the card, when there
            is a card to open) */}
        <NameTag
          {...(hasContent
            ? {
                type: "button",
                onClick: () => onToggle(entry.individual_id),
                "aria-expanded": isExpanded,
              }
            : {})}
          className="flex items-center flex-1 min-w-0 text-left"
        >
          {entry.profile?.profile_picture_url ? (
            <img
              src={entry.profile.profile_picture_url}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-[40px] h-[40px] rounded-full object-cover border border-dupontGold mr-[14px] flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span aria-hidden="true" className="w-[40px] h-[40px] rounded-full flex items-center justify-center mr-[14px] text-[17px] font-display font-bold flex-shrink-0" style={{ backgroundColor: '#9BBEC7', color: 'rgba(192, 64, 64, 0.55)' }}>
              {initial}
            </span>
          )}

          {/* Spans, not divs: a <button> may only hold inline content. As
              flex items they lay out exactly as the divs did. */}
          <span className="block flex-1 text-[18px] font-body text-dupontBlue dark:text-darkText min-w-0">
            <FormatDisplayName entry={entry} />
          </span>

          {hasContent && (
            <span className="block text-[14px] text-dupontGold ml-[8px] mr-[16px]" aria-hidden="true">
              {isExpanded ? "\u25B2" : "\u25BC"}
            </span>
          )}
        </NameTag>

        {/* Right side: Rolodex note input (under the name on a phone) */}
        <div className="flex items-center flex-shrink-0 w-full pl-[54px] sm:pl-0 sm:w-[240px]" onClick={(e) => e.stopPropagation()}>
          <span className="text-[16px] mr-[8px] opacity-40" title="Rolodex note">&#128221;</span>
          <input
            type="text"
            placeholder={
              notesState === "ready"
                ? "Add a note..."
                : notesState === "loading"
                  ? "Loading note..."
                  : "Notes unavailable"
            }
            aria-label={`Rolodex note for ${entry.legal_name}`}
            value={draft}
            onChange={handleNoteChange}
            disabled={notesState !== "ready"}
            maxLength={500}
            className="flex-1 text-[14px] font-body italic text-dupontBlue/80 dark:text-darkText/70 bg-dupontGold/[0.04] dark:bg-darkGold/[0.04] border border-dupontGold/15 dark:border-darkGold/10 focus:border-dupontGold/50 focus:bg-white dark:focus:bg-darkCard rounded-[6px] outline-none px-[10px] py-[6px] placeholder:text-dupontBlue/30 dark:placeholder:text-darkText/25 transition-all"
          />
        </div>
      </div>

      {/* Expanded profile card (accordion) */}
      {isExpanded && hasContent && (
        <div className="px-[16px] py-[12px] bg-white dark:bg-darkCard border-b border-dupontCreamDk dark:border-darkGold/10">
          <ProfileCard
            profile={{ ...entry.profile, name: entry.legal_name }}
            individualId={entry.individual_id}
          />
        </div>
      )}
    </div>
  );
}

function FormatDisplayName({ entry }) {
  const preferred = entry.profile?.preferred_name;
  if (preferred) {
    return (
      <>
        {entry.forenames}{" "}
        <span className="text-dupontGold italic">
          ("{preferred}")
        </span>{" "}
        {entry.surname}
      </>
    );
  }
  return <>{entry.legal_name}</>;
}

export default DirectoryRow;
